import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { authService } from './services/authService';
import { ToastProvider } from './context/ToastContext';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmail from './pages/VerifyEmail';
import ClientDashboard from './pages/client/ClientDashboard';
import AppointmentBooking from './pages/client/AppointmentBooking';
import AppointmentHistory from './pages/client/AppointmentHistory';
import BarberDashboard from './pages/barber/BarberDashboard';
import BarberProfile from './pages/barber/BarberProfile';
import SuperuserDashboard from './pages/superuser/SuperuserDashboard';
import BarberApprovalList from './pages/superuser/BarberApprovalList';
import BranchManagement from './pages/superuser/BranchManagement';
import BarberManagement from './pages/superuser/BarberManagement';
import CreateBarberPage from './pages/superuser/CreateBarberPage';
import ViewBarberPage from './pages/superuser/ViewBarberPage';
import EditBarberPage from './pages/superuser/EditBarberPage';
import BarberSchedulePage from './pages/superuser/BarberSchedulePage';
import ClientManagement from './pages/superuser/ClientManagement';
import CreateClientPage from './pages/superuser/CreateClientPage';
import EditClientPage from './pages/superuser/EditClientPage';
import ServiceTypeManagement from './pages/superuser/ServiceTypeManagement';
import Reports from './pages/superuser/Reports';
import WalkInSelectClientPage from './pages/barber/WalkInSelectClientPage';
import WalkInSelectServicePage from './pages/barber/WalkInSelectServicePage';
import WalkInConfirmPage from './pages/barber/WalkInConfirmPage';
import WalkInSuccessPage from './pages/barber/WalkInSuccessPage';
import PromotionManagement from './pages/admin/PromotionManagement';

import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

// Protected Route Component
interface ProtectedRouteProps {
    children: React.ReactNode;
    requiredRole?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
    const location = useLocation();
    const isAuthenticated = authService.isAuthenticated();
    const hasRequiredRole = requiredRole ? authService.hasRole(requiredRole) : true;

    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (!hasRequiredRole) {
        if (authService.hasRole('SUPERUSER')) {
            return <Navigate to="/admin/dashboard" replace />;
        } else if (authService.hasRole('BARBER')) {
            return <Navigate to="/barber/dashboard" replace />;
        } else if (authService.hasRole('CLIENT')) {
            return <Navigate to="/client/dashboard" replace />;
        }
        return <Navigate to="/login" replace />;
    }

    return <>{children}</>;
};

const App: React.FC = () => {
    return (
        <ToastProvider>
            <Router>
                <Routes>
                    {/* Public Routes */}
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />


                    {/* Client Routes */}
                    <Route
                        path="/client/dashboard"
                        element={
                            <ProtectedRoute requiredRole="CLIENT">
                                <ClientDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/client/book-appointment"
                        element={
                            <ProtectedRoute requiredRole="CLIENT">
                                <AppointmentBooking />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/book/:slug"
                        element={
                            <ProtectedRoute requiredRole="CLIENT">
                                <AppointmentBooking />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/client/history"
                        element={
                            <ProtectedRoute requiredRole="CLIENT">
                                <AppointmentHistory />
                            </ProtectedRoute>
                        }
                    />

                    {/* Barber Routes */}
                    <Route
                        path="/barber/dashboard"
                        element={
                            <ProtectedRoute requiredRole="BARBER">
                                <BarberDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/barber/profile"
                        element={
                            <ProtectedRoute requiredRole="BARBER">
                                <BarberProfile />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/barber/walk-in"
                        element={
                            <ProtectedRoute requiredRole="BARBER">
                                <WalkInSelectClientPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/barber/walk-in/service"
                        element={
                            <ProtectedRoute requiredRole="BARBER">
                                <WalkInSelectServicePage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/barber/walk-in/confirm"
                        element={
                            <ProtectedRoute requiredRole="BARBER">
                                <WalkInConfirmPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/barber/walk-in/success"
                        element={
                            <ProtectedRoute requiredRole="BARBER">
                                <WalkInSuccessPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Superuser Routes */}
                    <Route
                        path="/admin/dashboard"
                        element={
                            <ProtectedRoute requiredRole="SUPERUSER">
                                <SuperuserDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/barbers/pending"
                        element={
                            <ProtectedRoute requiredRole="SUPERUSER">
                                <BarberApprovalList />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/branches"
                        element={
                            <ProtectedRoute requiredRole="SUPERUSER">
                                <BranchManagement />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/barbers"
                        element={
                            <ProtectedRoute requiredRole="SUPERUSER">
                                <BarberManagement />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/create-barber"
                        element={
                            <ProtectedRoute requiredRole="SUPERUSER">
                                <CreateBarberPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/barbers/:id/view"
                        element={
                            <ProtectedRoute requiredRole="SUPERUSER">
                                <ViewBarberPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/barbers/:id/edit"
                        element={
                            <ProtectedRoute requiredRole="SUPERUSER">
                                <EditBarberPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/barbers/:id/schedule"
                        element={
                            <ProtectedRoute requiredRole="SUPERUSER">
                                <BarberSchedulePage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/clients"
                        element={
                            <ProtectedRoute requiredRole="SUPERUSER">
                                <ClientManagement />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/create-client"
                        element={
                            <ProtectedRoute requiredRole="SUPERUSER">
                                <CreateClientPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/edit-client/:id"
                        element={
                            <ProtectedRoute requiredRole="SUPERUSER">
                                <EditClientPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/reports"
                        element={
                            <ProtectedRoute requiredRole="SUPERUSER">
                                <Reports />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/services"
                        element={
                            <ProtectedRoute requiredRole="SUPERUSER">
                                <ServiceTypeManagement />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/promotions"
                        element={
                            <ProtectedRoute requiredRole="SUPERUSER">
                                <PromotionManagement />
                            </ProtectedRoute>
                        }
                    />

                    {/* Default Route */}
                    <Route path="/" element={<Navigate to="/login" replace />} />

                    {/* Unauthorized */}
                    <Route
                        path="/unauthorized"
                        element={
                            <div className="container" style={{ marginTop: '4rem', textAlign: 'center' }}>
                                <h1>Acceso No Autorizado</h1>
                                <p>No tienes permisos para acceder a esta página.</p>
                                <button className="btn btn-primary" onClick={() => window.location.href = '/login'}>
                                    Volver al inicio
                                </button>
                            </div>
                        }
                    />
                </Routes>
            </Router>
        </ToastProvider>
    );
};

export default App;
